import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useNavigate } from "@tanstack/react-router";
import {
  Calendar,
  ClipboardList,
  FileText,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  Mail,
  MessageSquare,
  Package,
  Search,
  Send,
  Tag,
  Users,
  Users2,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { useEffect, useMemo, useState } from "react";

interface Entry {
  label: string;
  keywords: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  shortcut?: string;
}

const ENTRIES: Entry[] = [
  {
    label: "Přehled",
    keywords: "dashboard domů uvod",
    to: "/dashboard",
    icon: LayoutDashboard,
    shortcut: "g d",
  },
  {
    label: "Termíny",
    keywords: "terminy seminars calendar",
    to: "/dashboard/terminy",
    icon: Calendar,
    shortcut: "g t",
  },
  {
    label: "Kontakty",
    keywords: "klienti kontakty crm",
    to: "/dashboard/klienti",
    icon: Users,
    shortcut: "g k",
  },
  { label: "Firmy", keywords: "firmy companies", to: "/dashboard/firmy", icon: Package },
  {
    label: "Přihlášky",
    keywords: "prihlasky enrollments",
    to: "/dashboard/prihlasky",
    icon: ClipboardList,
  },
  {
    label: "Produkty",
    keywords: "produkty products katalog",
    to: "/dashboard/produkty",
    icon: Package,
  },
  { label: "Lektoři", keywords: "lektori teachers", to: "/dashboard/lektori", icon: GraduationCap },
  { label: "Faktury", keywords: "faktury invoices", to: "/dashboard/faktury", icon: FileText },
  {
    label: "Docházka",
    keywords: "dochazka attendance",
    to: "/dashboard/dochazka",
    icon: ClipboardList,
  },
  {
    label: "Zpětná vazba",
    keywords: "zpetna vazba feedback",
    to: "/dashboard/zpetna-vazba",
    icon: MessageSquare,
  },
  { label: "Štítky", keywords: "tags tagy stitky", to: "/dashboard/marketing/tags", icon: Tag },
  {
    label: "Komunikace",
    keywords: "communications log",
    to: "/dashboard/marketing/komunikace",
    icon: MessageSquare,
  },
  {
    label: "E-mailové šablony",
    keywords: "email templates sablony",
    to: "/dashboard/marketing/email-templates",
    icon: Mail,
  },
  {
    label: "E-mailové kampaně",
    keywords: "campaigns kampane",
    to: "/dashboard/marketing/email-campaigns",
    icon: Send,
  },
  {
    label: "Statistiky e-mailů",
    keywords: "analytics analyzy statistiky emailu",
    to: "/dashboard/marketing/email-analytics",
    icon: ListChecks,
  },
  { label: "Uživatelé", keywords: "users admin", to: "/dashboard/admin/users", icon: Users2 },
  {
    label: "Audit log",
    keywords: "audit log historie",
    to: "/dashboard/admin/audit",
    icon: FileText,
  },
  { label: "Fronty úloh", keywords: "queues ops", to: "/dashboard/admin/queues", icon: ListChecks },
];

/** Globální command palette — Ctrl/Cmd+K otevře, Enter naviguje. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Ctrl+K / Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      // Escape already handled by Dialog
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return ENTRIES;
    return ENTRIES.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.keywords.toLowerCase().includes(q) ||
        (e.shortcut?.toLowerCase().includes(q) ?? false),
    );
  }, [query]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset highlight on query change
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  function go(to: string) {
    setOpen(false);
    setQuery("");
    navigate({ to });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0" data-testid="command-palette">
        <DialogHeader className="border-b p-3">
          <DialogTitle className="sr-only">Rychlý skok</DialogTitle>
          <DialogDescription className="sr-only">
            Hledejte stránku nebo použijte šipky pro navigaci.
          </DialogDescription>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setSelectedIdx((i) => Math.min(filtered.length - 1, i + 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setSelectedIdx((i) => Math.max(0, i - 1));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const target = filtered[selectedIdx];
                  if (target) go(target.to);
                }
              }}
              placeholder="Hledat stránku…"
              className="border-0 shadow-none focus-visible:ring-0"
              data-testid="command-palette-input"
            />
          </div>
        </DialogHeader>
        <ul className="max-h-[320px] overflow-y-auto py-1" data-testid="command-palette-list">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Žádné výsledky.</li>
          ) : (
            filtered.map((e, idx) => {
              const Icon = e.icon;
              const selected = idx === selectedIdx;
              return (
                <li key={e.to}>
                  <button
                    type="button"
                    onClick={() => go(e.to)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition ${
                      selected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
                    }`}
                    data-testid={`command-palette-item-${e.to}`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {e.label}
                    </span>
                    {e.shortcut && (
                      <span className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                        {e.shortcut}
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="border-t p-2 text-xs text-muted-foreground">
          <kbd className="rounded border bg-muted px-1">↑</kbd>{" "}
          <kbd className="rounded border bg-muted px-1">↓</kbd> pro pohyb ·{" "}
          <kbd className="rounded border bg-muted px-1">Enter</kbd> pro otevření ·{" "}
          <kbd className="rounded border bg-muted px-1">Esc</kbd> zavřít
        </div>
      </DialogContent>
    </Dialog>
  );
}
